import {
    Box,
    CircularProgress,
    Collapse,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useNotify from '../../../hooks/useNotify';
import {
    getBookingItems,
    updateBookingItemCheckinNote,
} from '@openapi/booking-items/booking-items';

const ExpandableBookingItems = ({ expanded, bookingId, timeslotId, disabled }) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editingNotes, setEditingNotes] = useState({});
    const [loaded, setLoaded] = useState(false);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const result = await getBookingItems({
                optionalId: bookingId,
                timeslotId: timeslotId || undefined,
                page: 0,
                pageSize: 1000,
            });
            setItems(result.bookingItems || []);
            setLoaded(true);
        } catch (err) {
            notifyError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleNoteChange = (itemId, value) => {
        setEditingNotes((prev) => ({ ...prev, [itemId]: value }));
    };

    const handleNoteBlur = async (itemId) => {
        if (editingNotes[itemId] === undefined) return;
        const original = items?.find((i) => i.id === itemId)?.checkinNote ?? '';
        if (editingNotes[itemId] === original) {
            setEditingNotes((prev) => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            return;
        }

        try {
            const updated = await updateBookingItemCheckinNote(
                itemId,
                { checkinNote: editingNotes[itemId] },
                { timeslotId: timeslotId || undefined },
            );
            notifySuccess(t('bookingitems.notifications.checkinNote.updated'));
            setEditingNotes((prev) => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            setItems((prev) =>
                prev.map((item) =>
                    item.id === itemId ? { ...item, checkinNote: updated.checkinNote } : item,
                ),
            );
        } catch (err) {
            notifyError(err);
        }
    };

    return (
        <TableRow>
            <TableCell
                colSpan={9}
                sx={{
                    py: 0,
                    px: 2,
                    border: expanded ? undefined : 0,
                }}
            >
                <Collapse
                    in={expanded}
                    timeout="auto"
                    // unmountOnExit resets all local state (items, loading, editingNotes)
                    // when the row collapses, so page/sort changes don't need manual cleanup
                    unmountOnExit
                    onEnter={() => {
                        if (!loaded) fetchItems();
                    }}
                >
                    <Box sx={{ py: 1 }}>
                        {loading ? (
                            <CircularProgress size={20} />
                        ) : items?.length > 0 ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            {t('bookingitems.columns.ticketSeatName')}
                                        </TableCell>
                                        <TableCell>
                                            {t('bookingitems.columns.ticketTypeName')}
                                        </TableCell>
                                        <TableCell>
                                            {t('bookingitems.columns.ticketOptionName')}
                                        </TableCell>
                                        <TableCell>{t('bookingitems.columns.note')}</TableCell>
                                        <TableCell>
                                            {t('bookingitems.columns.checkinNote')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>{item.ticketSeatName || '-'}</TableCell>
                                            <TableCell>{item.ticketTypeName}</TableCell>
                                            <TableCell>{item.ticketOptionName}</TableCell>
                                            <TableCell>{item.note || ''}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    disabled={disabled}
                                                    size="small"
                                                    fullWidth
                                                    value={
                                                        editingNotes[item.id] ??
                                                        item.checkinNote ??
                                                        ''
                                                    }
                                                    onChange={(e) =>
                                                        handleNoteChange(item.id, e.target.value)
                                                    }
                                                    onBlur={() => handleNoteBlur(item.id)}
                                                    placeholder={t(
                                                        'bookingitems.columns.checkinNote',
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {t('bookingitems.noBookingForThisSeat')}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};

export default ExpandableBookingItems;
